import { useCallback, useEffect, useState } from 'react'
import { useAuth } from './useAuth'
import { apiService, type Event, type EventAnalytics } from '../services/api'
import { mapWithConcurrency } from '../lib/concurrency'

export type AnalyticsTimeRange = '30d' | '90d' | '6m' | '1y' | 'all'
export type AnalyticsSport = 'ALL' | 'FOOTBALL' | 'BASEBALL'

export interface DrillPerformance {
  drillId: string
  name: string
  unit: string
  /** Result-count weighted mean of every event's average for this drill. */
  averageValue: number
  /** Best across events — min for time drills, max for everything else. */
  bestValue: number
  resultCount: number
}

export interface PositionBreakdown {
  position: string
  /** Participant entries, not distinct athletes: one athlete in two events counts twice. */
  count: number
  /** Share of all participant entries, as a percentage. */
  share: number
}

export interface AnalyticsEventSummary {
  id: string
  name: string
  date: string
  participantCount: number
}

/**
 * What the totals below could NOT see. A partial total presented as a whole is
 * the exact property this page was fixed for (#152): a confident number the
 * user cannot tell is wrong. Anything counted here must be shown to the user.
 */
export interface AnalyticsGaps {
  /** Events whose analytics failed for a non-403 reason (429, 5xx, network). */
  events: number
  /** Event-list pages that failed, so an unknown number of events is missing. */
  listPages: number
}

export interface AnalyticsSummary {
  totalEvents: number
  totalAthletes: number
  totalResults: number
  /** completed / all participants, as a percentage. `null` when there are no participants. */
  completionRate: number | null
  drillPerformance: DrillPerformance[]
  positionBreakdown: PositionBreakdown[]
  recentEvents: AnalyticsEventSummary[]
  gaps: AnalyticsGaps
}

const EVENTS_PAGE_SIZE = 100
const RECENT_EVENT_COUNT = 5

/**
 * Roles `/api/analytics/events/:id` can ever answer for. The route 403s
 * anything that is not the event's own organizer, a COACH, or an ADMIN
 * (`backend/src/routes/analytics.ts`), so a SCOUT, PARENT, or ATHLETE will
 * receive 403 for every event they ask about, forever.
 *
 * Without this the page would tell them "no analytics data yet", which is a
 * false statement about the data rather than a true one about their
 * permissions — the same species of confidently-wrong output as #152 itself.
 */
export const ANALYTICS_CAPABLE_ROLES: ReadonlySet<string> = new Set([
  'EVENT_ORGANIZER',
  'COACH',
  'ADMIN',
])

export function canViewAnalytics(role: string | undefined): boolean {
  return role !== undefined && ANALYTICS_CAPABLE_ROLES.has(role)
}

/**
 * How many analytics requests may be in flight at once.
 *
 * The backend rate-limits every client to 100 requests/minute globally
 * (`backend/src/server.ts`), and this hook issues one request per event on top
 * of whatever else the SPA is doing. A pool keeps the fan-out from arriving as
 * one burst. It does NOT make the total request count smaller, so it cannot on
 * its own stop a large organizer from being throttled — that is why every
 * throttled event is counted into `gaps` and shown, rather than dropped.
 *
 * The durable fix is a single server-side rollup endpoint; see the comment on
 * `useAnalyticsSummary`.
 */
const ANALYTICS_CONCURRENCY = 4

const TIME_RANGE_DAYS: Record<Exclude<AnalyticsTimeRange, 'all'>, number> = {
  '30d': 30,
  '90d': 90,
  '6m': 182,
  '1y': 365,
}

/**
 * Time units, where a *lower* value is the better one. Matched exactly after
 * normalising, because a substring test cannot be used here: `s` is a substring
 * of almost everything, and `in` (inches) is a substring of `min`.
 */
const TIME_UNITS = new Set([
  's',
  'sec',
  'secs',
  'second',
  'seconds',
  'ms',
  'msec',
  'millisecond',
  'milliseconds',
  'min',
  'mins',
  'minute',
  'minutes',
])

/**
 * Decides which end of the range is "best" when aggregating a drill across
 * events.
 *
 * This is deliberately *broader* than the backend's `isTimeDrill` in
 * `backend/src/routes/analytics.ts`, which tests `unit.includes('sec')` and so
 * gets a unit of `s` wrong — the demo fixtures use exactly that, and a real
 * seed could too. A follow-up is filed to have the backend send an explicit
 * `lowerIsBetter` flag on each `drillStats` entry so neither side has to guess;
 * when that lands, delete this function and read the flag.
 *
 * Until then the two rules can disagree: the backend picks the per-event best,
 * this picks which of those to keep. Being right here is strictly better than
 * matching a known-wrong rule.
 */
export function isLowerBetter(drillName: string, unit: string): boolean {
  const normalizedUnit = unit.trim().toLowerCase().replace(/\.$/, '')
  if (TIME_UNITS.has(normalizedUnit) || normalizedUnit.includes('sec')) {
    return true
  }

  const name = drillName.toLowerCase()
  return name.includes('time') || name.includes('dash') || name.includes('shuttle')
}

const round = (value: number, places = 2) => {
  const factor = 10 ** places
  return Math.round(value * factor) / factor
}

const participantTotal = (participation: EventAnalytics['participation']) =>
  participation.registered +
  participation.checkedIn +
  participation.completed +
  participation.noShow

export function withinTimeRange(
  startDate: string,
  timeRange: AnalyticsTimeRange,
  now = new Date()
) {
  if (timeRange === 'all') {
    return true
  }

  const start = new Date(startDate).getTime()
  if (Number.isNaN(start)) {
    return false
  }

  // Lower bound only: an event scheduled ahead of today still belongs to the
  // organizer's current window, and excluding it would make the page disagree
  // with the events list for no honest reason.
  const cutoff = now.getTime() - TIME_RANGE_DAYS[timeRange] * 24 * 60 * 60 * 1000
  return start >= cutoff
}

export function aggregateAnalytics(
  events: Event[],
  analytics: EventAnalytics[],
  gaps: AnalyticsGaps = { events: 0, listPages: 0 }
): AnalyticsSummary {
  let totalAthletes = 0
  let totalCompleted = 0
  let totalResults = 0

  const drills = new Map<
    string,
    { name: string; unit: string; resultCount: number; weightedSum: number; best: number | null }
  >()
  const positions = new Map<string, number>()

  for (const entry of analytics) {
    totalAthletes += participantTotal(entry.participation)
    totalCompleted += entry.participation.completed

    for (const drillStat of entry.drillStats) {
      totalResults += drillStat.totalResults

      // The backend reports zeroed stats for a drill with no results; folding
      // those in would drag every average down and make `bestValue` 0.
      if (drillStat.totalResults === 0) {
        continue
      }

      const key = drillStat.drill.id
      const existing = drills.get(key) ?? {
        name: drillStat.drill.name,
        unit: drillStat.drill.unit,
        resultCount: 0,
        weightedSum: 0,
        best: null,
      }

      existing.resultCount += drillStat.totalResults
      existing.weightedSum += drillStat.averageValue * drillStat.totalResults
      existing.best =
        existing.best === null
          ? drillStat.bestValue
          : isLowerBetter(existing.name, existing.unit)
            ? Math.min(existing.best, drillStat.bestValue)
            : Math.max(existing.best, drillStat.bestValue)

      drills.set(key, existing)
    }

    for (const position of entry.positionBreakdown) {
      positions.set(position.position, (positions.get(position.position) ?? 0) + position.count)
    }
  }

  const positionTotal = Array.from(positions.values()).reduce((sum, count) => sum + count, 0)

  const analyticsById = new Map(analytics.map(entry => [entry.event.id, entry]))

  return {
    totalEvents: events.length,
    totalAthletes,
    totalResults,
    completionRate: totalAthletes > 0 ? round((totalCompleted / totalAthletes) * 100, 1) : null,
    drillPerformance: Array.from(drills.entries())
      .map(([drillId, drill]) => ({
        drillId,
        name: drill.name,
        unit: drill.unit,
        averageValue: round(drill.weightedSum / drill.resultCount),
        bestValue: drill.best ?? 0,
        resultCount: drill.resultCount,
      }))
      .sort((a, b) => b.resultCount - a.resultCount),
    positionBreakdown: Array.from(positions.entries())
      .map(([position, count]) => ({
        position,
        count,
        share: positionTotal > 0 ? round((count / positionTotal) * 100, 1) : 0,
      }))
      .sort((a, b) => b.count - a.count),
    recentEvents: events
      .slice()
      .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime())
      .slice(0, RECENT_EVENT_COUNT)
      .map(event => {
        const participation = analyticsById.get(event.id)?.participation
        return {
          id: event.id,
          name: event.name,
          date: event.startDate,
          participantCount: participation ? participantTotal(participation) : 0,
        }
      }),
    gaps,
  }
}

/**
 * Aggregates the signed-in user's analytics from the endpoints that actually
 * exist. There is no global "all my events" rollup route, so this pages the
 * role-scoped events list and asks `/analytics/events/:id` about each one.
 *
 * Two rules keep the fan-out from reintroducing #152's defect in a new shape:
 *
 * 1. **Only a 403 may be dropped.** That is the backend saying "not your
 *    event" — a legitimate reason for a row to be absent. Every other failure
 *    (429 from the shared 100/min rate limit, 5xx, network) is counted into
 *    `gaps` and surfaced, because a silently-omitted event turns the totals
 *    into a confident undercount that changes between reloads.
 * 2. **A failed event-list page is a gap too.** An `{ error }` response is a
 *    *fulfilled* promise, so it must be inspected rather than spread — treating
 *    it as an empty page would report "200 events" for a 250-event org.
 *
 * The right long-term fix is one server-side rollup request instead of N; that
 * is a backend change and is filed separately.
 */
export function useAnalyticsSummary(timeRange: AnalyticsTimeRange, sport: AnalyticsSport) {
  const { user } = useAuth()
  // Depend on the id, not the user object: a provider that returns a fresh
  // object each render would otherwise re-run the effect on every state update
  // and never settle.
  const userId = user?.id ?? null
  const role = user?.role
  const authorized = canViewAnalytics(role)
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [reloadToken, setReloadToken] = useState(0)

  const refetch = useCallback(() => setReloadToken(token => token + 1), [])

  useEffect(() => {
    if (!userId) {
      return
    }

    // Every request would come back 403. Don't spend the rate limit on it, and
    // don't let the result masquerade as "no data".
    if (!authorized) {
      setSummary(null)
      setError(null)
      setLoading(false)
      return
    }

    let cancelled = false

    const fetchAllVisibleEvents = async (): Promise<{ events: Event[]; failedPages: number }> => {
      // `followable`: the server returns only events whose analytics this
      // caller may read. This used to list every event on the platform and
      // request analytics for each, counting the 403s as "not yours" — one
      // wasted request and console error per foreign event (gate 5).
      const firstPage = await apiService.getEvents({
        page: 1,
        limit: EVENTS_PAGE_SIZE,
        followable: true,
      })
      if (firstPage.error) {
        // Without page 1 there is no total and no baseline; nothing honest can
        // be rendered, so this is a hard failure rather than a gap.
        throw new Error(firstPage.error)
      }

      const firstPageEvents = firstPage.data?.events || []
      const totalPages = firstPage.data?.pagination?.pages || 0

      if (totalPages <= 1) {
        return { events: firstPageEvents, failedPages: 0 }
      }

      const pageNumbers = Array.from({ length: totalPages - 1 }, (_, index) => index + 2)
      const rest = await mapWithConcurrency(pageNumbers, ANALYTICS_CONCURRENCY, page =>
        apiService.getEvents({ page, limit: EVENTS_PAGE_SIZE, followable: true }).catch(() => ({
          error: 'Network Error',
          data: undefined,
        }))
      )

      const events = [...firstPageEvents]
      let failedPages = 0

      for (const response of rest) {
        // An `{ error }` body is a *resolved* promise. Spreading `data?.events
        // || []` here would turn a failed page into "that page was empty".
        if (response.error || !response.data) {
          failedPages++
          continue
        }
        events.push(...(response.data.events || []))
      }

      return { events, failedPages }
    }

    const load = async () => {
      setLoading(true)
      setError(null)

      try {
        const { events: allEvents, failedPages } = await fetchAllVisibleEvents()

        const filteredEvents = allEvents.filter(
          event =>
            (sport === 'ALL' || event.sport === sport) &&
            withinTimeRange(event.startDate, timeRange)
        )

        const analyticsResponses = await mapWithConcurrency(
          filteredEvents,
          ANALYTICS_CONCURRENCY,
          event =>
            apiService
              .getEventAnalytics(event.id)
              .catch(() => ({ error: 'Network Error', data: undefined, status: undefined }))
        )

        const includedEvents: Event[] = []
        const includedAnalytics: EventAnalytics[] = []
        let unreadableEvents = 0

        filteredEvents.forEach((event, index) => {
          const response = analyticsResponses[index]
          if (response?.data) {
            includedEvents.push(event)
            includedAnalytics.push(response.data)
            return
          }

          // 403 is the backend declining to show an event this user may not
          // see — the scoping working as designed, not a gap in the data.
          // Anything else (429, 5xx, 404, network) means we do not know what
          // this event holds, and the user must be told.
          if (response?.status !== 403) {
            unreadableEvents++
          }
        })

        if (!cancelled) {
          setSummary(
            aggregateAnalytics(includedEvents, includedAnalytics, {
              events: unreadableEvents,
              listPages: failedPages,
            })
          )
        }
      } catch {
        if (!cancelled) {
          // No fallback figures: an unreadable API must look unreadable.
          setSummary(null)
          setError('Failed to load analytics')
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    load()

    return () => {
      cancelled = true
    }
  }, [userId, authorized, timeRange, sport, reloadToken])

  return { summary, loading, error, authorized, refetch }
}
